import TryAgainIcon from '@components/icons/Jobs.svg';
import TrashBin from '@components/icons/TrashBin.svg';
import DownloadIcon from '@components/icons/Download.svg';
import { AbsoluteSmallSpinner } from '@components/core-ui/spinners/Spinner';
import { audioTypes } from '@helpers/audioTypes';
import React, { useState } from 'react';
import { useUploadContext } from '../UploadProvider';
import { Icon } from './components/Icon';
import { handleRemove } from './helpers/handleRemove';
import { makeSubtitle } from './helpers/subtitle';
import { truncateName } from './helpers/truncateName';
import type { TProps } from './types/TProps';
import { IconButtonDelete } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonDelete';

const errorMessageClassName =
  'text-[var(--color-job-status-error)] text-left py-[5px] pb-2.5 text-xs leading-[18px] font-normal';

const getRootClassName = (error: boolean) =>
  [
    'file-preview w-full p-3 flex flex-col gap-2 shadow-sm cursor-inherit',
    error
      ? 'border border-dashed border-[var(--color-job-status-error)] text-[var(--color-danger)]'
      : 'border border-dashed border-[var(--color-tint)] text-[var(--color-text-muted)]',
  ].join(' ');

export const FilePreview: React.FC<TProps> = ({
  item,
  allowRemove = true,
  allowRetry = true,
  allowDownload = false,
  actionSlot,
  bottomSlot,
  onRemoveRemote,
  onDownload,
  removeOptions,
}) => {
  const { removeFile, startUpload } = useUploadContext();
  const { meta } = item;
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadClick = async () => {
    if (!onDownload || isDownloading) {
      return;
    }
    setIsDownloading(true);
    try {
      await onDownload(meta);
    } finally {
      setIsDownloading(false);
    }
  };

  const status = meta.status;
  if (meta.status === 'removed') {
    return null;
  }
  const erroredState = status === 'error' || meta.extra?.analysisStatus === 'error';
  const processingState = status === 'preparing' || status === 'uploading' || meta.extra?.analysisStatus === 'progress';
  const showRemove = allowRemove && (!processingState || erroredState);
  const iconKind = erroredState ? 'error' : processingState ? 'progress' : 'ok';

  const fileType = (meta.name || '').split('.').pop()?.toLowerCase();
  const isAudioFile = audioTypes.includes(`.${fileType}`);

  return (
    <div className={getRootClassName(erroredState)}>
      <div className="w-full flex items-center justify-between gap-[10px]">
        <Icon kind={iconKind} isAudioFile={isAudioFile} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="truncate font-medium" title={meta.name}>
              {truncateName(meta.name)}
            </div>
          </div>
          <div className="text-xs truncate text-left">{makeSubtitle(meta)}</div>
        </div>
        <div className="flex items-center gap-2">
          {actionSlot}

          {allowDownload && !processingState && status !== 'error' && onDownload && (
            <button
              type="button"
              className="!h-[30px] !w-[30px] grid place-items-center flex-shrink-0 text-[var(--color-text-muted)] hover:text-[var(--color-link)] disabled:pointer-events-none"
              onClick={() => void handleDownloadClick()}
              disabled={isDownloading}
              title="Download"
            >
              {isDownloading ? (
                <span className="relative block h-5 w-5">
                  <AbsoluteSmallSpinner />
                </span>
              ) : (
                <DownloadIcon />
              )}
            </button>
          )}

          {status === 'error' && allowRetry && (
            <button
              type="button"
              className="!h-[30px] !w-[30px] grid place-items-center flex-shrink-0"
              onClick={() => startUpload(meta.id)}
              title="Retry"
            >
              <TryAgainIcon />
            </button>
          )}

          {showRemove && (
            <IconButtonDelete
              className={`!h-[30px] !w-[30px] grid place-items-center rounded-md hover:bg-[var(--color-danger)] hover:text-[var(--color-fixed-white)] ${erroredState ? 'text-[var(--color-danger)]' : 'text-[var(--color-text-muted)]'}`}
              onClick={() =>
                handleRemove({
                  meta,
                  onRemoveRemote,
                  removeLocal: (id, opts) => removeFile(id, { purge: opts?.purge !== false }),
                  removeOptions,
                })
              }
              title="Remove"
            >
              <TrashBin />
            </IconButtonDelete>
          )}
        </div>
      </div>

      {status === 'error' && meta.extra?.analysisStatus !== 'error' ? (
        <div className={errorMessageClassName}>{meta.errorMessage || 'Upload error'}</div>
      ) : null}

      {meta.extra?.analysisStatus === 'error' && meta.extra?.analysisErrorMessage ? (
        <div className={errorMessageClassName}>{meta.extra.analysisErrorMessage}</div>
      ) : meta.extra?.analysisStatus === 'error' ? (
        <div className={errorMessageClassName}>
          There was a problem analyzing your audio file. Please ensure the file will play in the audio player on your
          device. If playback is successful and the file is audible, please try uploading again. If the issue persists,
          please contact our{' '}
          <a
            href="https://support.voicescript.ai/portal/en/home"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            support team
          </a>{' '}
          for further assistance.
        </div>
      ) : null}

      {bottomSlot}
    </div>
  );
};
